/**
 * groundDecals.ts — BETA 22.70 (Weapon Feel Beta B).
 *
 * Temporary weapon-aftermath floor marks: scorch rings, splats, foam, smears.
 * Parallel to deathDecals.ts (which stays sprite-based for corpses).
 *
 * PERF DESIGN: each decal is STAMPED ONCE onto a small offscreen canvas at
 * spawn (procedural jagged blotches with baked random variation), then drawn
 * per-frame as a single drawImage + alpha fade — per-frame cost is image
 * blits, not path fills (the 20.77 "no per-particle path work" discipline).
 * Ring-buffer capped at 64, evict-oldest, frustum culled.
 */

export type GroundDecalKind = 'scorch' | 'splat' | 'foam' | 'smear' | 'frost';

interface GroundDecal {
  x: number;
  y: number;
  size: number;          // world-unit radius
  bornAt: number;        // state.time seconds
  ttl: number;
  stamp: HTMLCanvasElement;
}

const MAX_DECALS = 64;
const decals: GroundDecal[] = [];

const KIND_TTL: Record<GroundDecalKind, number> = {
  scorch: 7, splat: 6, foam: 5, smear: 4, frost: 5,
};

/** Bake one decal stamp. Called once per spawn — random variation is baked. */
function makeStamp(kind: GroundDecalKind, sizeWu: number): HTMLCanvasElement {
  const px = Math.max(24, Math.min(128, Math.round(sizeWu * 2.4)));
  const c = document.createElement('canvas');
  c.width = px; c.height = px;
  const g = c.getContext('2d')!;
  const cx = px / 2, cy = px / 2, r = px * 0.42;

  const blob = (fill: string, radius: number, jag: number, lobes: number) => {
    g.fillStyle = fill;
    g.beginPath();
    for (let i = 0; i <= lobes; i++) {
      const a = (i / lobes) * Math.PI * 2;
      const rr = radius * (1 - jag + Math.random() * jag * 2);
      const x = cx + Math.cos(a) * rr, y = cy + Math.sin(a) * rr * 0.82; // slight top-down squash
      if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
    }
    g.closePath();
    g.fill();
  };

  switch (kind) {
    case 'scorch': {
      // Charred jagged blotch + ash ring + a few baked ember speckles.
      blob('rgba(20,15,10,0.62)', r, 0.35, 9);
      blob('rgba(36,26,16,0.55)', r * 0.66, 0.3, 7);
      blob('rgba(10,8,6,0.7)', r * 0.38, 0.4, 6);
      g.strokeStyle = 'rgba(122,45,0,0.35)';
      g.lineWidth = Math.max(1, px * 0.03);
      g.beginPath(); g.arc(cx, cy, r * 0.8, 0, Math.PI * 2); g.stroke();
      for (let i = 0; i < 5; i++) {
        const a = Math.random() * Math.PI * 2, d = r * (0.3 + Math.random() * 0.6);
        g.fillStyle = i % 2 ? 'rgba(194,58,0,0.55)' : 'rgba(255,160,60,0.4)';
        g.fillRect(cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.82, 2, 2);
      }
      break;
    }
    case 'splat': {
      blob('rgba(70,50,20,0.55)', r, 0.4, 8);
      blob('rgba(96,70,28,0.5)', r * 0.55, 0.35, 6);
      for (let i = 0; i < 6; i++) {
        const a = Math.random() * Math.PI * 2, d = r * (0.8 + Math.random() * 0.5);
        blobAt(g, cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.82, px * 0.05, 'rgba(70,50,20,0.5)');
      }
      break;
    }
    case 'foam': {
      blob('rgba(240,226,190,0.4)', r, 0.3, 9);
      for (let i = 0; i < 10; i++) {
        const a = Math.random() * Math.PI * 2, d = r * Math.random();
        blobAt(g, cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.82, px * (0.03 + Math.random() * 0.04), 'rgba(250,242,220,0.5)');
      }
      break;
    }
    case 'smear': {
      g.save();
      g.translate(cx, cy);
      g.rotate(Math.random() * Math.PI);
      g.fillStyle = 'rgba(120,30,25,0.4)';
      g.beginPath();
      g.ellipse(0, 0, r, r * 0.3, 0, 0, Math.PI * 2);
      g.fill();
      g.restore();
      break;
    }
    case 'frost': {
      // BETA 22.76 — Brain Freeze: pale ice patch + white crackle fractures
      // radiating from the impact. Cold blue, nothing like the warm scorch.
      blob('rgba(150,200,235,0.34)', r, 0.32, 9);
      blob('rgba(215,238,255,0.4)', r * 0.5, 0.28, 7);
      g.strokeStyle = 'rgba(240,250,255,0.7)';
      g.lineWidth = Math.max(1, px * 0.02);
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2 + Math.random() * 0.4;
        const len = r * (0.55 + Math.random() * 0.45);
        g.beginPath();
        g.moveTo(cx, cy);
        g.lineTo(cx + Math.cos(a) * len, cy + Math.sin(a) * len * 0.82);
        g.stroke();
      }
      break;
    }
  }
  return c;
}

function blobAt(g: CanvasRenderingContext2D, x: number, y: number, radius: number, fill: string) {
  g.fillStyle = fill;
  g.beginPath();
  g.arc(x, y, radius, 0, Math.PI * 2);
  g.fill();
}

export function spawnGroundDecal(kind: GroundDecalKind, x: number, y: number, sizeWu: number, now: number): void {
  if (decals.length >= MAX_DECALS) decals.shift();  // evict oldest
  decals.push({ x, y, size: sizeWu, bornAt: now, ttl: KIND_TTL[kind], stamp: makeStamp(kind, sizeWu) });
}

export function clearGroundDecals(): void {
  decals.length = 0;
}

/** Draw all live decals. World→screen self-converting like renderDeathDecals. */
export function renderGroundDecals(
  ctx: CanvasRenderingContext2D,
  camera: { x: number; y: number; zoom: number },
  viewportWidth: number,
  viewportHeight: number,
  now: number,
): void {
  if (decals.length === 0) return;
  for (let i = decals.length - 1; i >= 0; i--) {
    const d = decals[i];
    const age = now - d.bornAt;
    if (age >= d.ttl) { decals.splice(i, 1); continue; }
    const sx = (d.x - camera.x) * camera.zoom + viewportWidth / 2;
    const sy = (d.y - camera.y) * camera.zoom + viewportHeight / 2;
    const drawR = d.size * camera.zoom;
    if (sx < -drawR || sx > viewportWidth + drawR || sy < -drawR || sy > viewportHeight + drawR) continue;
    // Full strength for the first 65% of life, fade out over the rest.
    const t = age / d.ttl;
    ctx.globalAlpha = t < 0.65 ? 1 : 1 - (t - 0.65) / 0.35;
    ctx.drawImage(d.stamp, sx - drawR, sy - drawR, drawR * 2, drawR * 2);
  }
  ctx.globalAlpha = 1;
}
